import { useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useT, type Translations } from '@/shared/i18n'
import {
  QR_TYPES,
  QrInputError,
  buildPayload,
  createInitialForm,
  qrAnalytics,
  qrAnalyticsKey,
  qrEntryFromSearch,
  qrTypeSearch,
  type ErrorCorrection,
  type FormState,
  type QrLandingSource,
  type QrType,
  type WifiEncryption,
} from '../model/qrPayload'

const AUTO_GENERATION_DELAY_MS = 200
const PREVIEW_SIZE = 256

type TypeOption = { value: QrType; label: string }
type PreviewStatus = 'pending' | 'valid' | 'stale' | 'invalid'

interface GeneratedQr {
  payload: string
  previewUrl: string
  snapshot: FormState
  generationId: number
  generatedAt: number
}

const correctionOptions: Array<{ value: ErrorCorrection; label: string }> = [
  { value: 'L', label: 'L 7%' },
  { value: 'M', label: 'M 15%' },
  { value: 'Q', label: 'Q 25%' },
  { value: 'H', label: 'H 30%' },
]

interface QrGeneratorToolProps {
  /** Reflect the chosen type in /qr-kod?type=…; the home page embeds the tool without it. */
  syncUrl?: boolean
}

export function QrGeneratorTool({ syncUrl = false }: QrGeneratorToolProps) {
  const t = useT()
  const generationIdRef = useRef(0)
  const mountedRef = useRef(true)
  const previewUrlRef = useRef<string | null>(null)
  const [form, setForm] = useState<FormState>(() => createInitialForm())
  const [landingSource, setLandingSource] =
    useState<QrLandingSource>('direct')
  const [entryReady, setEntryReady] = useState(false)
  const [generated, setGenerated] = useState<GeneratedQr | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewStatus, setPreviewStatus] = useState<PreviewStatus>('pending')
  const [retryNonce, setRetryNonce] = useState(0)
  const [isDownloading, setIsDownloading] = useState(false)
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  const contrast = useMemo(
    () => contrastRatio(form.foreground, form.background),
    [form.foreground, form.background],
  )
  const contrastOk = contrast >= 4.5
  const typeOptions = buildTypeOptions(t)
  const textDensity = useMemo(() => getTextDensity(form.text), [form.text])

  useEffect(() => {
    if (syncUrl) {
      const entry = qrEntryFromSearch(window.location.search)
      setLandingSource(entry.landingSource)
      setForm((current) => {
        if (entry.type === current.type) return current
        generationIdRef.current += 1
        return { ...current, type: entry.type }
      })
    }
    setEntryReady(true)
  }, [syncUrl])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationIdRef.current += 1
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!entryReady) return
    const snapshot = { ...form }
    const generationId = generationIdRef.current + 1
    generationIdRef.current = generationId
    setPreviewStatus((current) => (
      current === 'valid' || current === 'stale' ? 'stale' : 'pending'
    ))
    setError(null)
    const analytics = qrAnalytics(snapshot, landingSource)
    const analyticsKey = qrAnalyticsKey(snapshot.type, landingSource)
    let started = false
    let settled = false

    const terminalError = (code: string) => {
      if (!started || settled) return
      settled = true
      toolError(analyticsKey, code, analytics)
    }

    const timer = window.setTimeout(async () => {
      const startedAt = performance.now()
      started = true
      setPreviewStatus('pending')
      toolInvoke(analyticsKey, analytics)

      try {
        if (contrastRatio(snapshot.foreground, snapshot.background) < 4.5) {
          throw new QrInputError('low_contrast', t.qr.errors.lowContrast)
        }

        const payload = buildPayload(snapshot, t.qr.errors)
        if (import.meta.env.VITE_QRKA_E2E === 'true') {
          await waitForPreviewTestGate(generationId)
        }
        const previewDataUrl = await QRCode.toDataURL(
          payload,
          qrOptions(snapshot, PREVIEW_SIZE),
        )
        const previewUrl = URL.createObjectURL(pngDataUrlToBlob(previewDataUrl))

        if (settled || !mountedRef.current || generationId !== generationIdRef.current) {
          URL.revokeObjectURL(previewUrl)
          terminalError('superseded')
          return
        }

        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = previewUrl
        setGenerated({
          payload,
          previewUrl,
          snapshot,
          generationId,
          generatedAt: Date.now(),
        })
        setPreviewStatus('valid')
        settled = true
        toolSuccess(analyticsKey, performance.now() - startedAt, analytics)
      } catch (cause) {
        const qrError = normalizeError(cause)
        if (settled || !mountedRef.current || generationId !== generationIdRef.current) {
          terminalError('superseded')
          return
        }

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current)
          previewUrlRef.current = null
        }
        setGenerated(null)
        setError(localizeQrError(qrError, t.qr.errors))
        setPreviewStatus('invalid')
        terminalError(qrError.code)
      }
    }, AUTO_GENERATION_DELAY_MS)

    return () => {
      window.clearTimeout(timer)
      terminalError('superseded')
    }
  }, [entryReady, form, landingSource, retryNonce, t.qr.errors])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    generationIdRef.current += 1
    setPreviewStatus((current) => current === 'valid' ? 'stale' : current)
    setError(null)
    setForm((current) => ({ ...current, [key]: value }))
  }

  function selectType(type: QrType) {
    if (syncUrl) window.history.replaceState(null, '', `/qr-kod${qrTypeSearch(type)}`)
    setLandingSource('direct')
    update('type', type)
  }

  async function download(format: 'png' | 'svg') {
    if (!generated || previewStatus !== 'valid' || isDownloading) return

    const accepted = generated
    const startedAt = performance.now()
    const analytics = qrAnalytics(accepted.snapshot, landingSource)
    const analyticsKey = qrAnalyticsKey(accepted.snapshot.type, landingSource)
    setIsDownloading(true)
    toolInvoke(analyticsKey, analytics)

    try {
      const options = qrOptions(accepted.snapshot, accepted.snapshot.size)
      const href = format === 'png'
        ? await QRCode.toDataURL(accepted.payload, options)
        : URL.createObjectURL(new Blob([
          await QRCode.toString(accepted.payload, { ...options, type: 'svg' }),
        ], { type: 'image/svg+xml;charset=utf-8' }))

      if (accepted.generationId !== generationIdRef.current) {
        if (format === 'svg') URL.revokeObjectURL(href)
        throw new QrInputError('superseded_export', 'superseded_export')
      }

      const link = document.createElement('a')
      link.download = `qrka-${accepted.snapshot.type}-${accepted.generatedAt}.${format}`
      link.href = href
      link.click()
      if (format === 'svg') {
        window.setTimeout(() => URL.revokeObjectURL(href), 1000)
      }
      toolSuccess(analyticsKey, performance.now() - startedAt, analytics)
    } catch (cause) {
      const errorCode = cause instanceof QrInputError && cause.code === 'superseded_export'
        ? 'superseded'
        : `${format}_export_failed`
      if (errorCode !== 'superseded') {
        setError(t.qr.errors.generationFailed)
      }
      toolError(analyticsKey, errorCode, analytics)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-6">
      <Card className="min-w-0 rounded-lg lg:col-start-1">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div
            aria-label={t.qr.typeSelectorLabel}
            className="flex flex-wrap gap-2"
            role="radiogroup"
          >
            {typeOptions.map((option) => (
              <button
                aria-checked={form.type === option.value}
                key={option.value}
                className={cn(
                  'min-h-9 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  form.type === option.value
                    ? 'border-foreground/30 bg-foreground text-background shadow-sm'
                    : 'bg-background hover:bg-accent/60',
                )}
                onClick={() => selectType(option.value)}
                role="radio"
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="ym-disable-keys ym-hide-content">
            <PayloadFields form={form} t={t} textDensity={textDensity} update={update} />
          </div>

          <p className="text-xs leading-5 text-muted-foreground">{t.qr.data.description}</p>
        </CardContent>
      </Card>

      <aside
        aria-label={t.qr.preview.title}
        className="min-w-0 lg:sticky lg:top-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start"
      >
        <Card className="rounded-lg">
          <CardContent className="p-4 sm:p-5">
            <div className="mx-auto grid aspect-square w-full max-w-48 place-items-center rounded-lg border bg-white p-2 sm:max-w-56 lg:max-w-72 lg:p-4">
              {generated ? (
                <img
                  alt={t.qr.preview.imageAlt}
                  className={cn(
                    'ym-hide-content block size-full',
                    previewStatus !== 'valid' && 'opacity-55',
                  )}
                  data-error-correction={generated.snapshot.correction}
                  data-generated-type={generated.snapshot.type}
                  data-generated-size={generated.snapshot.size}
                  data-generation-id={generated.generationId}
                  data-testid="qr-preview"
                  height={PREVIEW_SIZE}
                  src={generated.previewUrl}
                  width={PREVIEW_SIZE}
                />
              ) : (
                <div className="grid aspect-square w-full place-items-center rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500">
                  {t.qr.preview.empty}
                </div>
              )}
            </div>

            {error && (
              <div
                className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
              >
                {error}
              </div>
            )}

            <div
              className="mt-3 text-center text-sm text-muted-foreground"
              data-preview-status={previewStatus}
              data-testid="qr-status"
              role="status"
            >
              {previewStatus === 'valid'
                ? t.qr.preview.ready
                : previewStatus === 'invalid'
                  ? t.qr.preview.invalid
                  : previewStatus === 'stale'
                    ? t.qr.preview.stale
                    : t.qr.preview.generating}
            </div>

            <div className="mt-3 grid gap-2">
              {previewStatus === 'invalid' && (
                <Button
                  className="w-full"
                  onClick={() => setRetryNonce((current) => current + 1)}
                  type="button"
                >
                  {t.qr.preview.retry}
                </Button>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  disabled={!generated || previewStatus !== 'valid' || isDownloading}
                  onClick={() => download('png')}
                  type="button"
                >
                  {t.qr.preview.downloadPng}
                </Button>
                <Button
                  disabled={!generated || previewStatus !== 'valid' || isDownloading}
                  onClick={() => download('svg')}
                  type="button"
                  variant="outline"
                >
                  {t.qr.preview.downloadSvg}
                </Button>
              </div>
            </div>

            <p className="mt-3 text-center text-xs leading-5 text-muted-foreground">
              {t.qr.preview.description}
            </p>
          </CardContent>
        </Card>
      </aside>

      <Card className="min-w-0 rounded-lg lg:col-start-1">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <CardTitle>{t.qr.appearance.title}</CardTitle>
            <Button
              aria-expanded={appearanceOpen}
              className="shrink-0 lg:hidden"
              onClick={() => setAppearanceOpen((current) => !current)}
              size="sm"
              type="button"
              variant="outline"
            >
              {appearanceOpen ? t.qr.appearance.hide : t.qr.appearance.show}
            </Button>
          </div>
          <CardDescription>
            {t.qr.appearance.description}
          </CardDescription>
        </CardHeader>
        <CardContent
          className={cn(
            'gap-5 md:grid-cols-2 lg:grid',
            appearanceOpen ? 'grid' : 'hidden',
          )}
        >
          <label className="space-y-2">
            <span className="text-sm font-medium">{t.qr.appearance.foreground}</span>
            <input
              className="h-11 w-full rounded-md border bg-background p-1"
              onChange={(event) => update('foreground', event.target.value)}
              type="color"
              value={form.foreground}
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">{t.qr.appearance.background}</span>
            <input
              className="h-11 w-full rounded-md border bg-background p-1"
              onChange={(event) => update('background', event.target.value)}
              type="color"
              value={form.background}
            />
          </label>
          <label className="space-y-2">
            <span className="flex items-center justify-between gap-3 text-sm font-medium">
              {t.qr.appearance.size}
              <span className="tabular-nums text-muted-foreground">{form.size}px</span>
            </span>
            <input
              className="w-full accent-primary"
              max={1024}
              min={256}
              onChange={(event) => update('size', Number(event.target.value))}
              step={64}
              type="range"
              value={form.size}
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">{t.qr.appearance.correction}</span>
            <select
              className="h-11 w-full rounded-md border bg-background px-3"
              onChange={(event) => update('correction', event.target.value as ErrorCorrection)}
              value={form.correction}
            >
              {correctionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div
            className={cn(
              'md:col-span-2 rounded-lg border px-4 py-3 text-sm',
              contrastOk
                ? 'border-emerald-500/35 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100'
                : 'border-destructive/35 bg-destructive/10 text-destructive',
            )}
          >
            {t.qr.appearance.contrast}: {contrast.toFixed(2)}:1. {contrastOk
              ? t.qr.appearance.contrastOk
              : t.qr.appearance.contrastBad}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function PayloadFields({
  form,
  t,
  textDensity,
  update,
}: {
  form: FormState
  t: Translations
  textDensity: TextDensity
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void
}) {
  if (form.type === 'url') {
    return (
      <label className="space-y-2">
        <span className="text-sm font-medium">{t.qr.fields.url}</span>
        <input
          className="h-11 w-full rounded-md border bg-background px-3"
          onChange={(event) => update('url', event.target.value)}
          placeholder={t.qr.fields.urlPlaceholder}
          type="url"
          value={form.url}
        />
      </label>
    )
  }

  if (form.type === 'text') {
    return (
      <div className="space-y-2">
        <label className="space-y-2">
          <span className="text-sm font-medium">{t.qr.fields.text}</span>
          <textarea
            className="min-h-32 w-full rounded-md border bg-background px-3 py-2"
            onChange={(event) => update('text', event.target.value)}
            placeholder={t.qr.fields.textPlaceholder}
            value={form.text}
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{t.qr.textDensity.counter(textDensity.chars, textDensity.bytes)}</span>
          <span>{t.qr.textDensity.hint}</span>
        </div>
        {textDensity.level !== 'ok' && (
          <div
            className={cn(
              'rounded-md border px-3 py-2 text-sm',
              textDensity.level === 'warning'
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100'
                : 'border-destructive/40 bg-destructive/10 text-destructive',
            )}
          >
            {textDensity.level === 'warning'
              ? t.qr.textDensity.warning
              : t.qr.textDensity.danger}
          </div>
        )}
      </div>
    )
  }

  if (form.type === 'wifi') {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-2">
          <span className="text-sm font-medium">{t.qr.fields.wifiSsid}</span>
          <input
            className="h-11 w-full rounded-md border bg-background px-3"
            onChange={(event) => update('wifiSsid', event.target.value)}
            placeholder={t.qr.fields.wifiSsidPlaceholder}
            value={form.wifiSsid}
          />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-medium">{t.qr.fields.wifiPassword}</span>
          <input
            className="h-11 w-full rounded-md border bg-background px-3"
            onChange={(event) => update('wifiPassword', event.target.value)}
            placeholder={t.qr.fields.wifiPasswordPlaceholder}
            type="password"
            value={form.wifiPassword}
          />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-medium">{t.qr.fields.wifiEncryption}</span>
          <select
            className="h-11 w-full rounded-md border bg-background px-3"
            onChange={(event) => update('wifiEncryption', event.target.value as WifiEncryption)}
            value={form.wifiEncryption}
          >
            <option value="WPA">{t.qr.fields.wifiWpa}</option>
            <option value="WEP">{t.qr.fields.wifiWep}</option>
            <option value="nopass">{t.qr.fields.wifiOpen}</option>
          </select>
        </label>
        <label className="flex items-center gap-3 rounded-md border bg-background px-3 py-2">
          <input
            checked={form.wifiHidden}
            className="size-4 accent-primary"
            onChange={(event) => update('wifiHidden', event.target.checked)}
            type="checkbox"
          />
          <span className="text-sm font-medium">{t.qr.fields.wifiHidden}</span>
        </label>
      </div>
    )
  }

  if (form.type === 'vcard') {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <TextInput label={t.qr.fields.vcardName} onChange={(value) => update('vcardName', value)} value={form.vcardName} />
        <TextInput label={t.qr.fields.vcardOrg} onChange={(value) => update('vcardOrg', value)} value={form.vcardOrg} />
        <TextInput label={t.qr.fields.vcardPhone} onChange={(value) => update('vcardPhone', value)} value={form.vcardPhone} />
        <TextInput label={t.qr.fields.vcardEmail} onChange={(value) => update('vcardEmail', value)} value={form.vcardEmail} />
        <TextInput label={t.qr.fields.vcardUrl} onChange={(value) => update('vcardUrl', value)} placeholder={t.qr.fields.urlPlaceholder} value={form.vcardUrl} />
      </div>
    )
  }

  if (form.type === 'email') {
    return (
      <div className="grid gap-4">
        <TextInput label={t.qr.fields.emailAddress} onChange={(value) => update('emailAddress', value)} value={form.emailAddress} />
        <TextInput label={t.qr.fields.emailSubject} onChange={(value) => update('emailSubject', value)} value={form.emailSubject} />
        <label className="space-y-2">
          <span className="text-sm font-medium">{t.qr.fields.emailBody}</span>
          <textarea
            className="min-h-24 w-full rounded-md border bg-background px-3 py-2"
            onChange={(event) => update('emailBody', event.target.value)}
            value={form.emailBody}
          />
        </label>
      </div>
    )
  }

  if (form.type === 'sms') {
    return (
      <div className="grid gap-4">
        <TextInput label={t.qr.fields.smsPhone} onChange={(value) => update('smsPhone', value)} value={form.smsPhone} />
        <label className="space-y-2">
          <span className="text-sm font-medium">{t.qr.fields.smsBody}</span>
          <textarea
            className="min-h-24 w-full rounded-md border bg-background px-3 py-2"
            onChange={(event) => update('smsBody', event.target.value)}
            value={form.smsBody}
          />
        </label>
      </div>
    )
  }

  return (
    <TextInput
      label={t.qr.fields.phone}
      onChange={(value) => update('phone', value)}
      placeholder={t.qr.fields.phonePlaceholder}
      value={form.phone}
    />
  )
}

function TextInput({
  label,
  onChange,
  placeholder,
  value,
}: {
  label: string
  onChange: (value: string) => void
  placeholder?: string
  value: string
}) {
  return (
    <label className="space-y-2">
      <span className="text-sm font-medium">{label}</span>
      <input
        className="h-11 w-full rounded-md border bg-background px-3"
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        value={value}
      />
    </label>
  )
}

type QrErrors = Translations['qr']['errors']

function buildTypeOptions(t: Translations): TypeOption[] {
  return QR_TYPES.map((type) => ({
    value: type,
    label: t.qr.typeOptions[type].label,
  }))
}

function qrOptions(form: FormState, width: number) {
  return {
    errorCorrectionLevel: form.correction,
    margin: 4,
    width,
    color: {
      dark: form.foreground,
      light: form.background,
    },
  }
}

async function waitForPreviewTestGate(generationId: number): Promise<void> {
  if (typeof window === 'undefined') return

  const testGate = (window as unknown as {
    __qrkaPreviewTestGate?: (id: number) => Promise<void>
  }).__qrkaPreviewTestGate
  await testGate?.(generationId)
}

function pngDataUrlToBlob(dataUrl: string): Blob {
  const separator = dataUrl.indexOf(',')
  if (separator < 0) {
    throw new QrInputError('generation_failed', 'generation_failed')
  }

  const binary = atob(dataUrl.slice(separator + 1))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return new Blob([bytes], { type: 'image/png' })
}

type TextDensityLevel = 'ok' | 'warning' | 'danger'

interface TextDensity {
  bytes: number
  chars: number
  level: TextDensityLevel
}

function getTextDensity(value: string): TextDensity {
  const trimmed = value.trim()
  const bytes = byteLength(trimmed)
  const chars = trimmed.length
  const level: TextDensityLevel = bytes >= 1600
    ? 'danger'
    : bytes >= 900
      ? 'warning'
      : 'ok'

  return { bytes, chars, level }
}

function byteLength(value: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length
  }

  return unescape(encodeURIComponent(value)).length
}

function contrastRatio(foreground: string, background: string): number {
  const fg = relativeLuminance(hexToRgb(foreground))
  const bg = relativeLuminance(hexToRgb(background))
  const lighter = Math.max(fg, bg)
  const darker = Math.min(fg, bg)
  return (lighter + 0.05) / (darker + 0.05)
}

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '')
  const value = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized

  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ]
}

function relativeLuminance([red, green, blue]: [number, number, number]): number {
  const normalize = (channel: number): number => {
    const normalized = channel / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4
  }
  const r = normalize(red)
  const g = normalize(green)
  const b = normalize(blue)

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function localizeQrError(error: QrInputError, errors: QrErrors): string {
  switch (error.code) {
    case 'low_contrast':
      return errors.lowContrast
    case 'preview_unavailable':
      return errors.previewUnavailable
    case 'invalid_url':
      return errors.invalidUrl
    case 'empty_vcard':
      return errors.emptyVcard
    case 'generation_failed':
      return errors.generationFailed
    case 'empty_payload':
    default:
      return error.message || errors.emptyPayload
  }
}

function normalizeError(cause: unknown): QrInputError {
  if (cause instanceof QrInputError) {
    return cause
  }

  return new QrInputError(
    'generation_failed',
    'generation_failed',
  )
}
